import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, Check, ClipboardList, Flag, Loader2, X } from 'lucide-react';
import { Disclosure, DisclosureToggle, NoteDisclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { Input } from '@/components/ui/input';
import { StatusPill } from '@/components/Status';
import { statusInk, type StatusTone } from '@/lib/status';
import { cn } from '@/lib/utils';
import {
  getDraftOrder,
  getDraftSignals,
  overrideDraftLineQty,
  setDraftLevers,
  setDraftLineReview,
  type DraftDetail,
  type DraftLever,
  type DraftLine,
  type DraftSignalRow,
  type DraftSummary,
} from '@/api/orderPrep';
import { Button } from '@/components/ui/button';
import { Can } from '@/auth/guards';
import { useAuth } from '@/auth/useAuth';
import { CreatePoAction } from './CreatePoAction';
import { fmtDate, money, unitCost } from './lib';
import { WorkNotice } from './workLib';

// ---------------------------------------------------------------------------
// The prep draft inside the To-order row: a summary chip on the row itself and
// an expandable section with the full proposal — lines, arithmetic, signals,
// review flags and review changes — plus what only Matt does here: override a
// qty, answer a flag, apply or undo a review change. The draft proposes; Mark ordered / Won't order stay the only
// exits from the queue.
// ---------------------------------------------------------------------------

const MODE_HINTS: Record<string, string> = {
  preorder: 'Sized daily from live preorder signals.',
  blind: 'Benchmark-sized against comparable lines and prior releases.',
  hybrid: 'Blind baseline, upgraded to live signals as products go live.',
};

// How far the draft has got: sized daily while the release is open (`info`,
// the run has it), a final proposal is ready to place (`success`).
const STATUS_META: Record<string, { label: string; tone: StatusTone }> = {
  building: { label: 'building', tone: 'neutral' },
  active: { label: 'sized', tone: 'info' },
  final: { label: 'final proposal', tone: 'success' },
  closed: { label: 'closed', tone: 'neutral' },
};

/** "~$1,234" when any cost underneath is estimated or missing. */
function estMoney(value: number | null, approx: boolean, currency: string): string {
  if (value == null) return '—';
  return `${approx ? '~' : ''}${money(value, currency)}`;
}

/**
 * The row chip: one glance = does a proposal exist, how big, what needs me.
 * Clicking it toggles the expanded prep section.
 */
export function DraftChip({ draft, expanded, currency, onToggle }: {
  draft: DraftSummary;
  expanded: boolean;
  currency: string;
  onToggle: () => void;
}) {
  const meta = STATUS_META[draft.status] ?? STATUS_META.building;
  const release = draft.counts.release;
  const restock = draft.counts.restock;
  const flagged = (release?.flagged ?? 0) + (restock?.flagged ?? 0);
  const unsized = (release?.unsized ?? 0) + (restock?.unsized ?? 0);
  const draftFlagged = draft.review?.verdict === 'flag';
  // A status pill inside one disclosure toggle: the pill says how far the
  // draft has got, the chevron is the only thing that opens it.
  return (
    <Hint content={`${MODE_HINTS[draft.mode] ?? draft.mode}${draft.refreshedAt ? ` Last sized ${fmtDate(draft.refreshedAt)}.` : ''}`}>
      <span className="inline-flex">
        <DisclosureToggle open={expanded} onToggle={onToggle}>
          <ClipboardList className="h-3 w-3" />
          <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
          {release && release.units > 0 && (
            <span className="tabular-nums">
              {release.units} units · {estMoney(release.estCost, release.costEstimated, currency)}
            </span>
          )}
          {restock && restock.lines > 0 && (
            <span className="tabular-nums">+{restock.units} restock</span>
          )}
          {unsized > 0 && (
            <span className={statusInk('warning')}>{unsized} unsized</span>
          )}
          {(flagged > 0 || draftFlagged) && (
            <span className={cn('inline-flex items-center gap-0.5', statusInk('warning'))}>
              <Flag className="h-3 w-3" />{flagged > 0 ? flagged : ''}
            </span>
          )}
        </DisclosureToggle>
      </span>
    </Hint>
  );
}

// ---------------------------------------------------------------------------
// Signal sparkline — the payoff of the daily snapshots. Sold is the solid
// line, baskets the faint one; both share one y-scale so their gap is honest.
// ---------------------------------------------------------------------------

function Sparkline({ sold, baskets }: { sold: number[]; baskets: number[] }) {
  const max = Math.max(1, ...sold, ...baskets);
  const w = 56;
  const h = 16;
  const path = (series: number[]) => {
    const n = series.length;
    if (n < 2) return '';
    return series
      .map((v, i) => `${((i / (n - 1)) * (w - 2) + 1).toFixed(1)},${(h - 1 - (v / max) * (h - 2)).toFixed(1)}`)
      .join(' ');
  };
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden>
      {baskets.length >= 2 && (
        <polyline points={path(baskets)} fill="none" stroke="currentColor" strokeWidth="1" className="text-muted-foreground/40" />
      )}
      {sold.length >= 2 && (
        <polyline points={path(sold)} fill="none" stroke="currentColor" strokeWidth="1.2" className="text-foreground" />
      )}
    </svg>
  );
}

function SignalCell({ line, history }: { line: DraftLine; history: DraftSignalRow[] }) {
  const s = line.signals;
  const latest = history[history.length - 1];
  if (!s && !latest) return <span className="text-muted-foreground/50">—</span>;
  const sold = latest?.sold ?? s?.sold ?? null;
  const baskets = latest?.baskets ?? s?.baskets ?? null;
  const notify = latest?.notifyMe ?? s?.notify_me ?? null;
  return (
    <Hint content={`Live preorder signal: ${sold ?? '?'} sold, ${baskets ?? '?'} in open baskets${notify != null ? `, ${notify} notify-me` : ''}${history.length > 1 ? ` — ${history.length} daily snapshots` : ''}`}>
      <span className="inline-flex items-center gap-1.5 tabular-nums">
        {history.length >= 2 && (
          <Sparkline
            sold={history.map((r) => r.sold ?? 0)}
            baskets={history.map((r) => r.baskets ?? 0)}
          />
        )}
        <span>
          {sold ?? '—'}<span className="text-muted-foreground"> +{baskets ?? '—'}b</span>
        </span>
      </span>
    </Hint>
  );
}

// ---------------------------------------------------------------------------
// Qty cell — display, and the one write Matt owns. A saved override becomes
// qty_source 'matt' (survives refreshes); the server clears any review flag on
// the line itself, because a changed number is unreviewed by definition.
// ---------------------------------------------------------------------------

function QtyCell({ line, orderId, onSaved }: {
  line: DraftLine;
  orderId: string;
  onSaved: () => Promise<void>;
}) {
  // The override is a POST the server gates on orders:write:drafts. Without it the
  // number still has to render — it just stops being a button.
  const canEdit = useAuth().can('orders:write:drafts');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!editing) {
    const label = line.qty == null
      ? <span className={statusInk('warning')}>—</span>
      : line.qty === 0
        ? <span className="text-muted-foreground line-through">skip</span>
        : <span>{line.qty}</span>;
    if (!canEdit) {
      return <span className="px-1.5 py-0.5 font-medium tabular-nums">{label}</span>;
    }
    return (
      <Hint content={
        line.qty == null
          ? 'Not sized yet; click to set a quantity.'
          : line.qtySource === 'matt' ? 'Manual override; refreshes keep it.' : 'Click to override.'
      }>
        <button
          type="button"
          className={cn(
            'rounded px-1.5 py-0.5 font-medium tabular-nums hover:bg-muted',
            line.qtySource === 'matt' && 'underline decoration-dotted underline-offset-2'
          )}
          onClick={() => { setDraft(line.qty == null ? '' : String(line.qty)); setError(null); setEditing(true); }}
        >
          {label}
        </button>
      </Hint>
    );
  }

  const parsed = draft.trim() === '' ? null : Number(draft);
  const valid = parsed != null && Number.isInteger(parsed) && parsed >= 0;
  const packWarning = valid && parsed > 0 && line.packQty && parsed % line.packQty !== 0
    ? `not a multiple of pack ${line.packQty}` : null;

  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Input
        type="number"
        min={0}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
        className="h-6 w-16 px-1.5 text-xs tabular-nums pointer-coarse:h-9 pointer-coarse:w-20"
      />
      <button
        type="button"
        aria-label="Save quantity"
        className="inline-flex items-center justify-center text-foreground hover:opacity-80 disabled:opacity-40 pointer-coarse:size-11"
        disabled={busy || !valid}
        onClick={async () => {
          if (parsed == null) return;
          setBusy(true);
          setError(null);
          try {
            await overrideDraftLineQty(orderId, line.id, parsed);
            await onSaved();
            setEditing(false);
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
      </button>
      <button type="button" aria-label="Cancel quantity edit" className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground pointer-coarse:size-11" onClick={() => setEditing(false)}>
        <X className="h-3.5 w-3.5" />
      </button>
      {packWarning && <span className={cn('text-xs', statusInk('warning'))}>{packWarning}</span>}
      {error && <span className={cn('text-xs', statusInk('danger'))}>{error}</span>}
    </span>
  );
}

/** The review agent's question on a line, and the one-click "the qty stands". */
function FlagCell({ line, orderId, onSaved }: {
  line: DraftLine;
  orderId: string;
  onSaved: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  if (line.reviewFlag !== 'flag') {
    return line.reviewFlag === 'ok'
      ? <Hint content="Reviewed."><Check className={cn('h-3.5 w-3.5 opacity-70', statusInk('success'))} /></Hint>
      : null;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Hint content="Flagged by the review agent.">
        <Flag className={cn('h-3.5 w-3.5 shrink-0', statusInk('warning'))} />
      </Hint>
      <Can code="orders:write:drafts">
      <Hint content="The quantity stands.">
        <button
          type="button"
          className="rounded border border-border/60 px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try { await setDraftLineReview(orderId, line, 'ok'); await onSaved(); }
            finally { setBusy(false); }
          }}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'stands'}
        </button>
      </Hint>
      </Can>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Review changes — the review's levers. A lever is a named batch of line
// targets ("Cut 9 slow lines"); Today's "Apply the review's changes" applies
// the recommended ones. Here each one can be applied or undone on its own, and
// the lines it would set carry an arrow to its target.
// ---------------------------------------------------------------------------

/** A lever touching a line, with the qty it sets. Keyed "kind line_key". */
interface LineLever { lever: DraftLever; qty: number }
const lineRef = (kind: string, lineKey: string) => `${kind} ${lineKey}`;

function signed(n: number, text: string): string {
  return `${n > 0 ? '+' : n < 0 ? '−' : '±'}${text}`;
}

const LEVER_FLOOR = 50;

function leverPill(l: DraftLever): { label: string; tone: StatusTone } {
  if (l.applied) return { label: 'applied', tone: 'success' };
  return l.default === 'apply' ? { label: 'recommended', tone: 'info' } : { label: 'optional', tone: 'neutral' };
}

function ReviewChanges({ levers, orderId, currency, onSaved }: {
  levers: DraftLever[];
  orderId: string;
  currency: string;
  onSaved: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  if (levers.length === 0) return null;
  const recommended = levers.filter((l) => l.default === 'apply' && !l.applied && l.missing_lines.length === 0);

  const run = async (id: string, keys: string[], apply: boolean) => {
    setBusy(id);
    try {
      await setDraftLevers(orderId, keys, apply);
      await onSaved();
      toast.success(apply ? 'Applied to the draft' : 'Undone; the lines are back to what they were');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not change the draft');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide">Review changes</span>
        {recommended.length > 1 && (
          <Can code="orders:write:drafts">
            <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={busy !== null}
              onClick={() => run('all', recommended.map((l) => l.key), true)}>
              {busy === 'all' && <Loader2 className="h-3 w-3 animate-spin" />}
              Apply {recommended.length} recommended
            </Button>
          </Can>
        )}
      </div>
      <ul className="divide-y divide-border/40 rounded-md border border-border/60 bg-card text-xs">
        {levers.map((l) => {
          const pill = leverPill(l);
          const gone = l.missing_lines.length;
          return (
            <li key={l.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5">
              <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
              <span className="min-w-0 flex-1 font-medium text-foreground">{l.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {l.lines} line{l.lines === 1 ? '' : 's'}
                {' · '}{signed(l.units, `${Math.abs(l.units)} units`)}
                {' · '}{l.unknown_cost_lines ? '~' : ''}{signed(l.cost, money(Math.abs(l.cost), currency))}
              </span>
              {gone > 0 && (
                <Hint content={`Not on the draft any more: ${l.missing_lines.join(', ')}`}>
                  <span className={statusInk('warning')}>{gone} line{gone === 1 ? '' : 's'} gone</span>
                </Hint>
              )}
              {!l.applied && !l.over_floor && gone === 0 && (
                <Hint content={`Moves the order by $${LEVER_FLOOR} or less now, so Today doesn't offer it.`}>
                  <span className="text-muted-foreground">under ${LEVER_FLOOR}</span>
                </Hint>
              )}
              <NoteDisclosure text={l.note ?? null} label="Why" />
              <Can code="orders:write:drafts">
                {l.applied ? (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" disabled={busy !== null}
                    onClick={() => run(l.key, [l.key], false)}>
                    {busy === l.key && <Loader2 className="h-3 w-3 animate-spin" />}Undo
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={busy !== null || gone > 0}
                    onClick={() => run(l.key, [l.key], true)}>
                    {busy === l.key && <Loader2 className="h-3 w-3 animate-spin" />}Apply
                  </Button>
                )}
              </Can>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Under a line's qty: where each unapplied lever would set it. */
function LeverTargets({ line, marks }: { line: DraftLine; marks: LineLever[] }) {
  const pending = marks.filter((m) => !m.lever.applied && m.qty !== line.qty);
  if (pending.length === 0) return null;
  return (
    <div className="flex flex-col gap-0.5">
      {pending.map((m) => (
        <Hint key={m.lever.key} content={`"${m.lever.label}" (${leverPill(m.lever).label}) sets this to ${m.qty}`}>
          <span className={cn('inline-flex items-center gap-0.5 px-1.5 tabular-nums', statusInk('info'))}>
            <ArrowRight className="h-3 w-3" />{m.qty === 0 ? 'skip' : m.qty}
          </span>
        </Hint>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lines table — one per kind. Release is the proposal; restock is the add-on
// tab whose second job is carrying the order over vendor minimums.
// ---------------------------------------------------------------------------

function LinesTable({ title, lines, orderId, currency, signalsByPid, leverMarks, onSaved }: {
  title: string;
  lines: DraftLine[];
  leverMarks: Map<string, LineLever[]>;
  orderId: string;
  currency: string;
  signalsByPid: Map<number, DraftSignalRow[]>;
  onSaved: () => Promise<void>;
}) {
  const anySignals = lines.some((l) => l.signals || (l.pid != null && signalsByPid.has(l.pid)));
  const units = lines.reduce((sum, l) => sum + (l.qty ?? 0), 0);
  const cost = lines.reduce((sum, l) => sum + (l.qty != null && l.unitCost != null ? l.qty * l.unitCost : 0), 0);
  const approx = lines.some((l) => l.costBasis === 'estimated' || ((l.qty ?? 0) > 0 && l.unitCost == null));

  return (
    <div>
      <div className="mb-1 flex items-baseline gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide">{title}</span>
        <span className="text-xs text-muted-foreground">
          {lines.length} products · {units} units · {estMoney(cost || null, approx, currency)}
        </span>
      </div>
      <div className="overflow-x-auto rounded-md border border-border/60">
        <table className="w-full min-w-[760px] text-xs">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-medium">
              <th>Product</th>
              <th className="w-24">Type</th>
              {anySignals && <th className="w-32">Signal</th>}
              <th className="w-20">Qty</th>
              <th className="w-14">Pack</th>
              <th className="w-20">Cost</th>
              <th className="w-20">Total</th>
              <th>Why</th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} className="border-t border-border/40 align-top [&>td]:px-2 [&>td]:py-1">
                <td>
                  <div className="max-w-[320px]">
                    <div className="truncate" title={line.description}>{line.description}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[
                        line.pid != null ? `pid ${line.pid}` : null,
                        line.itemnumber,
                        line.vendorSku,
                      ].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                </td>
                <td className="text-muted-foreground">{line.productType ?? '—'}</td>
                {anySignals && (
                  <td>
                    <SignalCell line={line} history={line.pid != null ? (signalsByPid.get(line.pid) ?? []) : []} />
                  </td>
                )}
                <td>
                  <QtyCell line={line} orderId={orderId} onSaved={onSaved} />
                  <LeverTargets line={line} marks={leverMarks.get(lineRef(line.kind, line.lineKey)) ?? []} />
                </td>
                <td className="tabular-nums text-muted-foreground">
                  {line.packQty ?? '—'}{line.minQty != null && line.minQty !== line.packQty ? ` / min ${line.minQty}` : ''}
                </td>
                <td className="tabular-nums">
                  {line.unitCost != null ? (
                    <Hint content={line.costBasis === 'estimated' ? 'Estimated cost (brand ratio) — no vendor sheet price yet' : 'Vendor cost'}>
                      <span>{line.costBasis === 'estimated' ? '~' : ''}{unitCost(line.unitCost)}</span>
                    </Hint>
                  ) : '—'}
                </td>
                <td className="tabular-nums">
                  {line.qty != null && line.unitCost != null ? money(line.qty * line.unitCost, currency) : '—'}
                </td>
                <td>
                  <div className="flex max-w-[320px] flex-col gap-0.5">
                    <NoteDisclosure text={line.rationale} label="Why" />
                    {line.reviewFlag === 'flag' && (
                      <NoteDisclosure text={line.reviewNote} label={<span className={statusInk('warning')}>Flag</span>} />
                    )}
                  </div>
                </td>
                <td className="text-right">
                  <FlagCell line={line} orderId={orderId} onSaved={onSaved} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The blind engine's cached homework: what each type was benchmarked against. */
function BenchmarkBlock({ draft }: { draft: DraftDetail }) {
  const types = draft.benchmark?.types;
  if (!Array.isArray(types) || types.length === 0) return null;
  return (
    <div className="text-xs">
      <Disclosure
        label={
          <>
            Benchmarks{draft.benchmark.designer ? ` · ${draft.benchmark.designer} cohort` : ''}
            {draft.benchmark.computed_at && <span className="opacity-60"> {fmtDate(draft.benchmark.computed_at)}</span>}
          </>
        }
      >
        <div className="overflow-x-auto rounded-md border border-border/60">
          <table className="w-full min-w-[480px] text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-medium">
                <th>Type</th><th className="w-24">Benchmark</th><th className="w-28">Window</th>
                <th className="w-16">Cohort</th><th>Note</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t, i) => (
                <tr key={i} className="border-t border-border/40 [&>td]:px-2 [&>td]:py-1">
                  <td>{t.product_type ?? '—'}</td>
                  <td className="tabular-nums">{t.benchmark ?? '—'}</td>
                  <td className="text-muted-foreground">{t.window ?? '—'}</td>
                  <td className="tabular-nums text-muted-foreground">{t.cohort_n ?? '—'}</td>
                  <td className="text-muted-foreground">{t.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Disclosure>
    </div>
  );
}

/**
 * The expanded prep section under a To-order row. Fetches the full draft and
 * the journey's signal history on mount; every write refetches both the detail
 * and (via onDraftsChanged) the summary chips above.
 */
export function OrderPrepDetail({ orderId, currency, supplierId, poId, onDraftsChanged }: {
  orderId: string;
  currency: string;
  supplierId: number | null;
  poId: number | null;
  onDraftsChanged: () => void;
}) {
  const [detail, setDetail] = useState<DraftDetail | null>(null);
  const [signals, setSignals] = useState<DraftSignalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Both reads below are `/api/order-prep/*` — orders:read:drafts. Without it this
  // section renders nothing rather than fetching and showing the 403.
  const canPlace = useAuth().can('orders:read:drafts');

  const load = useCallback(async () => {
    if (!canPlace) return;
    try {
      const [draft, signalRows] = await Promise.all([
        getDraftOrder(orderId),
        getDraftSignals(orderId),
      ]);
      setDetail(draft);
      setSignals(signalRows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the prep draft');
    } finally {
      setLoading(false);
    }
  }, [orderId, canPlace]);

  useEffect(() => { load(); }, [load]);

  const refetch = useCallback(async () => {
    await load();
    onDraftsChanged();
  }, [load, onDraftsChanged]);

  const signalsByPid = useMemo(() => {
    const map = new Map<number, DraftSignalRow[]>();
    for (const row of signals) {
      const list = map.get(row.pid) || [];
      list.push(row);
      map.set(row.pid, list);
    }
    return map;
  }, [signals]);

  const leverMarks = useMemo(() => {
    const map = new Map<string, LineLever[]>();
    for (const lever of detail?.levers ?? []) {
      for (const c of lever.changes) {
        const ref = lineRef(c.kind, c.line_key);
        map.set(ref, [...(map.get(ref) ?? []), { lever, qty: c.qty }]);
      }
    }
    return map;
  }, [detail]);

  if (!canPlace) return null;
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading the prep draft…
      </div>
    );
  }
  if (error) {
    return <WorkNotice tone="danger">{error}</WorkNotice>;
  }
  if (!detail) {
    return <div className="py-2 text-xs text-muted-foreground">No prep draft for this journey yet.</div>;
  }

  const release = detail.lines.filter((l) => l.kind === 'release');
  const restock = detail.lines.filter((l) => l.kind === 'restock');
  const review = detail.review;

  return (
    <div className="mt-1.5 space-y-2 rounded-md border border-border/40 bg-muted/20 p-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <Hint content={MODE_HINTS[detail.mode] ?? detail.mode}>
          <span className="font-medium text-foreground">{detail.mode} draft</span>
        </Hint>
        <StatusPill tone={(STATUS_META[detail.status] ?? STATUS_META.building).tone}>
          {(STATUS_META[detail.status] ?? STATUS_META.building).label}
        </StatusPill>
        {detail.refreshedAt && (
          <Hint content="When the daily run last recomputed these quantities.">
            <span>sized {fmtDate(detail.refreshedAt)}</span>
          </Hint>
        )}
        <Can code="orders:write:lifecycle">
          <CreatePoAction detail={detail} supplierId={supplierId} poId={poId} onChanged={refetch} />
        </Can>
      </div>

      {detail.status === 'building' && detail.blockedReason && (
        <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 text-xs', statusInk('warning'))}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium">Sizing incomplete</span>
          <NoteDisclosure text={detail.blockedReason} label="Why" />
        </div>
      )}

      {review?.summary && (
        <div className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 text-xs',
          statusInk(review.verdict === 'flag' ? 'warning' : 'neutral')
        )}>
          <Flag className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium">
            {review.verdict === 'flag' ? 'Review flagged' : 'Reviewed'}
            {review.trigger ? ` · ${String(review.trigger).replace(/_/g, ' ')}` : ''}
          </span>
          <NoteDisclosure text={review.summary} label="Review" />
        </div>
      )}

      <BenchmarkBlock draft={detail} />

      <ReviewChanges levers={detail.levers ?? []} orderId={orderId} currency={currency} onSaved={refetch} />

      {release.length > 0 && (
        <LinesTable
          title="Release"
          lines={release}
          orderId={orderId}
          currency={currency}
          signalsByPid={signalsByPid}
          leverMarks={leverMarks}
          onSaved={refetch}
        />
      )}
      {restock.length > 0 && (
        <LinesTable
          title="Restock add-on"
          lines={restock}
          orderId={orderId}
          currency={currency}
          signalsByPid={signalsByPid}
          leverMarks={leverMarks}
          onSaved={refetch}
        />
      )}
      {detail.lines.length === 0 && (
        <div className="text-xs text-muted-foreground">No lines yet.</div>
      )}

      <NoteDisclosure text={detail.notes} />
    </div>
  );
}
